/**
 * Pane-local scrolling for the Call Script layout's two panes — the Output "All fields"
 * board and the Call Script column (BV-2129).
 *
 * Neither pane may use scrollIntoView, for the reason documented on Transcript's
 * centerInTranscript: it moves every scrollable ancestor, and the panes share one with
 * the rest of the Review row, so centring a field would shift the whole row.
 */

/**
 * The element that actually scrolls `el`, searching upward.
 *
 * Which ancestor is bounded — and therefore scrolls — depends on the layout the pane is
 * mounted in, so it is found rather than assumed. Scrolling a non-scrollable element is
 * silently a no-op, which is exactly how "scroll to the failed field" once did nothing.
 *
 * Bounded deliberately: the walk stops before any ancestor that also contains the Call
 * Script column, because scrolling THAT shifts the whole Review row. A row inside the
 * script still finds the column's own body, which sits below that boundary. Returns null
 * rather than reaching past it.
 */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    if (node.querySelector('[data-testid="review-call-script"]')) return null;
    const style = getComputedStyle(node);
    const scrollable = /auto|scroll|overlay/.test(style.overflowY);
    if (scrollable && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

/** Centre `el` in its pane's scroller; a no-op when nothing bounded holds it. */
export function centerInScrollParent(el: HTMLElement): void {
  const container = findScrollParent(el);
  if (!container) return;
  // Geometry against the scroller, via getBoundingClientRect: offsetTop is relative to
  // the nearest POSITIONED ancestor, which is not necessarily the scroller.
  const top =
    container.scrollTop +
    (el.getBoundingClientRect().top - container.getBoundingClientRect().top) -
    container.clientHeight / 2 +
    el.clientHeight / 2;
  container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

/**
 * Centre `el` horizontally in the multi-code grid that holds it (BV-2742): the grid
 * scrolls sideways once the codes outgrow the column, so a jump to a code's cell has to
 * bring that column into view too. Bounded the same way as the vertical scroll — only
 * the grid's own `[data-matrix-scroller]` moves, never the Review row. A no-op outside a
 * grid, or when the grid does not scroll.
 */
export function centerInMatrixScroller(el: HTMLElement): void {
  const scroller = el.closest<HTMLElement>('[data-matrix-scroller]');
  if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
  const left =
    scroller.scrollLeft +
    (el.getBoundingClientRect().left - scroller.getBoundingClientRect().left) -
    scroller.clientWidth / 2 +
    el.clientWidth / 2;
  scroller.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
}

/**
 * Briefly tint `el` and fade it back out, so the eye lands on the row a reveal scrolled
 * to (BV-2742).
 *
 * Driven by the Web Animations API rather than a CSS class: `animate()` restarts on every
 * call, so a second click on the same field flashes again instead of being swallowed by a
 * class that is already applied. The first ~30% holds at full strength so the highlight
 * is still visible once the smooth scroll settles. A no-op where `animate` is missing
 * (jsdom).
 */
export function flashReveal(el: HTMLElement): void {
  const on = {
    backgroundColor: 'hsl(var(--primary) / 0.12)',
    boxShadow: '0 0 0 2px hsl(var(--primary) / 0.5)',
  };
  const off = { backgroundColor: 'transparent', boxShadow: '0 0 0 2px transparent' };
  el.animate?.([on, { ...on, offset: 0.3 }, off], { duration: 2000, easing: 'ease-out' });
}
